interface ContributionSegment {
  slug: string;
  label: string;
  color: string;
  value: number;
}

interface ContributionBarProps {
  segments: ContributionSegment[];
  maxTotal?: number;
  height?: number;
}

/**
 * Hand-laid stacked bar — segment width = value / maxTotal. We keep this
 * standalone (rather than reusing VerdictBar) because the segment shape here
 * carries a label/color/value triple, and VerdictBar is roadmap-verdict-shaped.
 *
 * maxTotal defaults to 5 (the max possible weighted total when all criteria
 * score 5 and weights sum to 1). Passing a tighter maxTotal — e.g. the max
 * total across the visible candidate set — makes the leader's bar full-width
 * and everyone else relative to them; useful for the "by how much?" read.
 */
export function ContributionBar({ segments, maxTotal = 5, height = 10 }: ContributionBarProps) {
  const total = segments.reduce((acc, s) => acc + s.value, 0);
  const cap = Math.max(maxTotal, total);

  return (
    <div
      className="relative overflow-hidden rounded-full"
      style={{
        height,
        background: "oklch(0.94 0.005 240)",
        boxShadow: "inset 0 0 0 1px oklch(0.91 0.008 240)",
      }}
    >
      <div className="flex h-full w-full">
        {segments.map((s) => {
          const widthPct = cap > 0 ? (s.value / cap) * 100 : 0;
          if (widthPct <= 0) return null;
          return (
            <div
              key={s.slug}
              title={`${s.label} · ${s.value.toFixed(2)}`}
              style={{
                width: `${widthPct}%`,
                background: s.color,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export type { ContributionSegment };
